// src/components/Control/robot/snapshots.ts
// Снимки с камер по кнопке (затвор на окне камеры), один контроллер на страницу.
// Куда сохранять — выбор в этом браузере («Настройки → Снимки с камер»):
//  - в браузер: кадр, который показан на экране, скачивается файлом сразу;
//  - на робот: нода snapshot на Pi сохраняет у себя свой последний кадр этой
//    камеры (полное качество, независимо от FPS передачи) с позой робота и углами
//    поворотной камеры; потом их выгружают в «Снимках» частью или все (ZIP).
//      robot/<id>/snap/cmd    <- {"cmd": "take", "cam", "label", "req"}
//      robot/<id>/snap/result -> {"req", "ok", "item"?, "error"?}
// «Набор» (метка: «зарядка», «дверь»…) идёт в имя файла / метаданные снимка —
// по нему снимки раскладываются по папкам в ZIP (готовая разметка для обучения).
// Серия: N снимков через интервал — пока страница открыта.
import * as zenoh from '@eclipse-zenoh/zenoh-ts';
import { Store } from './store';
import { EventsStore, notifier } from './events';
import { latestFrame } from './cameraFeed';
import type { CamId } from './RobotContext';
import { putJson } from '../../../hooks/useZenohSubscription';
import { loadPref, savePref } from '../../../types/features';

export type SnapDest = 'client' | 'robot';

export interface SnapItem {
  name: string;
  t: number;
  cam: CamId;
  label: string;
  w: number;
  h: number;
  bytes: number;
  robot?: string;
  pose?: { x: number; y: number; yaw: number } | null;
  pan_tilt?: { pan: number; tilt: number } | null;
  frame_age_ms?: number;
}

// robot/<id>/snap/state (нода snapshot)
export interface SnapState {
  count: number;
  used_bytes: number;
  free_bytes: number | null;
  max_bytes: number;
  labels: Record<string, number>;
  newest: number | null;
  cams: Record<CamId, boolean>;
  error: string | null;
}

export interface SnapshotsState {
  dest: SnapDest;
  label: string;
  seriesCount: number;      // 1 — одиночный снимок
  seriesInterval: number;   // с
  series: { cam: CamId; done: number; total: number; failed: number } | null;
  taken: number;            // снято с этой страницы
  last: { at: number; ok: boolean; text: string; cam: CamId } | null;  // подпись у затвора
  busy: boolean;
}

const RESULT_TIMEOUT_MS = 5000;
export const LABEL_MAX = 40;
const PREF = 'snap.prefs';

const two = (n: number, w = 2) => String(n).padStart(w, '0');
export function stamp(t: number) {
  const d = new Date(t);
  return `${d.getFullYear()}${two(d.getMonth() + 1)}${two(d.getDate())}-${two(d.getHours())}${two(d.getMinutes())}${two(d.getSeconds())}-${two(d.getMilliseconds(), 3)}`;
}
// Метка для имени файла / папки: без символов, которые нельзя в путях
export function safeName(s: string) {
  // eslint-disable-next-line no-control-regex
  return s.trim().replace(/[\\/:*?"<>|\x00-\x1f]+/g, '_').replace(/\s+/g, '_').slice(0, LABEL_MAX);
}

export function downloadBlob(blob: Blob, name: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

export class SnapshotController {
  readonly store: Store<SnapshotsState>;
  private session: zenoh.Session | null = null;
  private robotId = '';
  private sub: zenoh.Subscriber | null = null;
  private subFor: zenoh.Session | null = null;
  private pending = new Map<string, (r: { ok: boolean; item?: SnapItem; error?: string }) => void>();
  private timer: ReturnType<typeof setTimeout> | null = null;
  private notify: ReturnType<typeof notifier>;

  constructor(events: EventsStore) {
    this.notify = notifier(events);
    const p = loadPref<Partial<SnapshotsState>>(PREF, {});
    this.store = new Store<SnapshotsState>({
      dest: p.dest === 'client' ? 'client' : 'robot',
      label: typeof p.label === 'string' ? p.label : '',
      seriesCount: Math.max(1, Math.min(500, Number(p.seriesCount) || 1)),
      seriesInterval: Math.max(0.5, Math.min(60, Number(p.seriesInterval) || 2)),
      series: null, taken: 0, last: null, busy: false,
    });
  }

  setTarget(session: zenoh.Session | null, robotId: string) {
    if (session !== this.session || robotId !== this.robotId) {
      this.stopSeries();
      this.unsubscribe();
    }
    this.session = session;
    this.robotId = robotId;
  }

  configure(patch: Partial<Pick<SnapshotsState, 'dest' | 'label' | 'seriesCount' | 'seriesInterval'>>) {
    this.store.set(patch);
    const { dest, label, seriesCount, seriesInterval } = this.store.get();
    savePref(PREF, { dest, label, seriesCount, seriesInterval });
  }

  // Кнопка затвора: один снимок или серия (повторное нажатие во время серии — стоп)
  shoot(cam: CamId) {
    const st = this.store.get();
    if (st.series) {
      this.stopSeries();
      return;
    }
    if (st.seriesCount <= 1) {
      this.takeOne(cam);
      return;
    }
    this.store.set({ series: { cam, done: 0, total: st.seriesCount, failed: 0 } });
    this.seriesStep();
  }

  stopSeries() {
    if (this.timer) clearTimeout(this.timer);
    this.timer = null;
    const s = this.store.get().series;
    if (s) {
      this.store.set({ series: null });
      this.say(s.done > s.failed, `Серия: снято ${s.done - s.failed} из ${s.total}`, s.cam);
    }
  }

  private seriesStep() {
    const s = this.store.get().series;
    if (!s) return;
    const started = Date.now();
    this.takeOne(s.cam, true).then(ok => {
      const cur = this.store.get().series;
      if (!cur) return;
      const next = { ...cur, done: cur.done + 1, failed: cur.failed + (ok ? 0 : 1) };
      // Подряд одни ошибки (робот спит, нет кадров) — не стреляем дальше впустую
      if (next.done >= next.total || (next.failed >= 3 && next.failed === next.done)) {
        this.store.set({ series: next });
        this.stopSeries();
        return;
      }
      this.store.set({ series: next });
      const wait = Math.max(0, this.store.get().seriesInterval * 1000 - (Date.now() - started));
      this.timer = setTimeout(() => this.seriesStep(), wait);
    });
  }

  private say(ok: boolean, text: string, cam: CamId) {
    this.store.set({ last: { at: Date.now(), ok, text, cam } });
  }

  async takeOne(cam: CamId, quiet = false): Promise<boolean> {
    const { dest, label } = this.store.get();
    const err = (text: string) => {
      this.say(false, text, cam);
      // В серии ошибка видна у затвора; сообщение в истории — одно на серию не спамим
      if (!quiet || this.store.get().series?.failed === 0) this.notify(`Снимок не сделан: ${text}`, 'error');
      return false;
    };
    const camName = `камера ${Number(cam) + 1}`;
    if (dest === 'client') {
      if (!this.session) return err('нет связи');
      const f = latestFrame(this.session, `robot/${this.robotId}/cams/${cam}`);
      if (!f || Date.now() - f.at > 3000) return err(`нет кадров (${camName} не показывается или не передаёт)`);
      const tag = safeName(label);
      downloadBlob(f.blob, `${safeName(this.robotId)}-cam${Number(cam) + 1}${tag ? `-${tag}` : ''}-${stamp(f.at)}.jpg`);
      this.store.set(s => ({ taken: s.taken + 1 }));
      this.say(true, `Скачан · ${camName}${label ? ` · ${label}` : ''}`, cam);
      return true;
    }
    if (!this.session) return err('нет связи');
    this.store.set({ busy: true });
    try {
      await this.subscribe();
      const req = Math.random().toString(36).slice(2, 10);
      const result = new Promise<{ ok: boolean; item?: SnapItem; error?: string }>(resolve => {
        this.pending.set(req, resolve);
        setTimeout(() => {
          if (this.pending.delete(req)) resolve({ ok: false, error: 'робот не ответил (нода snapshot не запущена?)' });
        }, RESULT_TIMEOUT_MS);
      });
      const sent = await putJson(this.session, `robot/${this.robotId}/snap/cmd`, { cmd: 'take', cam, label: label.trim(), req });
      if (!sent) {
        this.pending.delete(req);
        return err('команда не отправлена');
      }
      const r = await result;
      if (!r.ok) return err(r.error || 'ошибка');
      this.store.set(s => ({ taken: s.taken + 1 }));
      this.say(true, `На роботе · ${camName}${label ? ` · ${label}` : ''}`, cam);
      return true;
    } finally {
      this.store.set({ busy: false });
    }
  }

  private async subscribe() {
    if (this.sub && this.subFor === this.session) return;
    this.unsubscribe();
    const session = this.session!;
    this.subFor = session;
    const sub = await session.declareSubscriber(`robot/${this.robotId}/snap/result`, {
      handler: (sample: zenoh.Sample) => {
        try {
          const msg = JSON.parse(sample.payload().toString());
          const done = this.pending.get(msg?.req);
          if (done) {
            this.pending.delete(msg.req);
            done(msg);
          }
        } catch {
          /* не JSON */
        }
      },
    });
    if (this.subFor !== session) sub.undeclare().catch(() => {});
    else this.sub = sub;
  }

  private unsubscribe() {
    this.sub?.undeclare().catch(() => {});
    this.sub = null;
    this.subFor = null;
  }

  dispose() {
    this.stopSeries();
    this.unsubscribe();
  }
}
